import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Fade,
  Slide,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Web as WebIcon,
  Smartphone as SmartphoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const APP_STORE_URL = 'https://apps.apple.com/us/app/veda-and-vine/id6757864055';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vedavine.healthyfood';

const AppChoicePopup = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOpenPopup = urlParams.get('isOpenPopup');
    
    if (isOpenPopup === 'true') {
      setOpen(true);
      // Clean URL without page reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    navigate('/shop');
  };

  const handleWebContinue = () => {
    setOpen(false);
    navigate('/shop');
  };

  const handleAppContinue = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      window.location.href = APP_STORE_URL;
    } else if (/android/.test(userAgent)) {
      window.location.href = PLAY_STORE_URL;
    } else {
      // Fallback to App Store for desktop
      window.location.href = APP_STORE_URL;
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={SlideTransition}
      keepMounted
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
        },
        '& .MuiPaper-root': {
          margin: 0,
          marginBottom: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderRadius: '16px 16px 0 0',
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px 16px 0 0',
          background: 'linear-gradient(135deg, #F5F2EB 0%, #FFFFFF 100%)',
          boxShadow: '0px -4px 20px rgba(201, 169, 97, 0.3)',
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        position: 'relative',
        '& .MuiTypography-root': {
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 600,
          fontSize: '2rem',
          color: '#1A1A1A',
        }
      }}>
        Choose Your Experience
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#6B6B6B',
            '&:hover': {
              color: '#C9A961',
              backgroundColor: 'rgba(201, 169, 97, 0.1)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3, px: 4 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            textAlign: 'center', 
            mb: 4,
            color: '#6B6B6B',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '1.1rem',
            lineHeight: 1.6
          }}
        >
          How would you like to experience Veda & Vine?
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Continue with Web Button */}
          <Button
            onClick={handleWebContinue}
            variant="contained"
            fullWidth
            size="medium"
            startIcon={<WebIcon />}
            sx={{
              py: 1.5,
              px: 3,
              backgroundColor: '#C9A961',
              color: '#FFFFFF',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: '0.05em',
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0px 6px 12px rgba(201, 169, 97, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#B89850',
                transform: 'translateY(-1px)',
                boxShadow: '0px 8px 16px rgba(201, 169, 97, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0)',
              }
            }}
          >
            Continue with Web
          </Button>

          {/* Continue with App Button */}
          <Button
            onClick={handleAppContinue}
            variant="outlined"
            fullWidth
            size="medium"
            startIcon={<SmartphoneIcon />}
            sx={{
              py: 1.5,
              px: 3,
              borderColor: '#C9A961',
              color: '#C9A961',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: '0.05em',
              borderRadius: 2,
              textTransform: 'none',
              borderWidth: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(201, 169, 97, 0.1)',
                borderColor: '#B89850',
                color: '#B89850',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              }
            }}
          >
            Continue with App
          </Button>
        </Box>

        <Typography 
          variant="caption" 
          sx={{ 
            textAlign: 'center', 
            display: 'block',
            mt: 4,
            color: '#9E9E9E',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.85rem'
          }}
        >
          Get the best experience with our mobile app
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default AppChoicePopup;
